"use client"

import { entryMriTierKey, unsoldMriTierKeys } from "@/data/mri-packages"
import {
  useState,
  useMemo,
  useDeferredValue,
  useRef,
  useEffect,
  useLayoutEffect,
} from "react"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import Link from "next/link"
import { CrossCatalogSuggestion } from "@/components/ui/cross-catalog-suggestion"
import { BiomarkersAiDrawer } from "@/components/search/biomarkers-ai-drawer"
import { aiAssistantEnabled } from "@/lib/release-flags"
import { hasAiPreviewActive } from "@/lib/ai-preview"
import { getPageTitle } from "@/lib/metadata"
import { ExpandableRow } from "@/components/ui/biomarker-disease-explorer"
import { MobileRailFilters } from "@/components/ui/mobile-rail-filters"
import { useHorizontalDragScroll } from "@/lib/use-horizontal-drag-scroll"
import {
  scrollSectionToTop,
  scrollResultsIntoView,
} from "@/lib/explorer-scroll"
import { useHideOnScrollDown } from "@/lib/use-hide-on-scroll-down"
import { SearchModeToggle } from "@/components/ui/search-mode-toggle"
import { renderCategoryIcon } from "@/lib/category-icons"
import {
  SearchIcon,
  CloseIcon,
  ChevronDownSmIcon,
  CheckCircleNeutralIcon,
  SparkleIcon,
} from "@/components/ui/icons"
import { ActionBadge } from "@/components/ui/action-badge"
import { cn } from "@/lib/utils"
import {
  buildPackageFilters,
  buildTagOnlyPackages,
  dropImagingFiltersForCountry,
  isBloodOnlyMarketBiomarker,
  orderFiltersForCountry,
  groupItemsByCmsCategory,
  PACKAGE_BADGE_COLOR,
  PACKAGE_FILTER_ORDER,
  SHORT_PACKAGE_LABEL_KEY,
} from "@/lib/categories"
import { cmsLocale } from "@/lib/cms"
import type { CMSBiomarkerListItem, CmsCategory } from "@/lib/cms"
import { useEditRegion } from "@/lib/edit/edit-region-registry"
import { EditableField } from "@/components/editor/editable-field"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { areSymptomsEnabled } from "@/i18n/config"
import { hasSymptomsPreviewActive } from "@/lib/symptoms-preview"
import { InsuranceSection } from "@/components/ui/insurance-section"
import { track } from "@/lib/analytics"
import { createPageSearcher } from "@/lib/search/page-filter"
import { findCrossCatalogMatches } from "@/lib/search/cross-catalog"
import {
  loadCrossLocaleAliases,
  type CrossLocaleAliasMap,
} from "@/lib/search/cross-locale-aliases-client"

/** Main package IDs get their own "Panels" checkbox group; everything else
 *  (add-ons) drops into the "Add-ons" group below. Mirrors CategoryFilter. */
const MAIN_PACKAGE_IDS = new Set([
  "core",
  "advanced",
  "pro",
  "blood",
  "prevention-starter",
])

/** Canonical package order (Core → Advanced → Pro → Blood → Follow-up →
 *  add-ons), so the "included in" chips read the same on every row instead of
 *  following each biomarker's arbitrary CMS `packages` order. Mirrors the
 *  symptoms page. */
const PACKAGE_ORDER_INDEX = new Map(
  PACKAGE_FILTER_ORDER.map((p, i) => [p.id, i])
)

const OTHER_SECTION_SLUG = "__other"

type BiomarkerSection = {
  slug: string
  id?: string
  name: string
  description: string
  icon?: CmsCategory["icon"]
  color?: string | null
  items: CMSBiomarkerListItem[]
}

/** How many add-ons show before the "show more" toggle. */
const ADDON_PREVIEW_COUNT = 3

// TODO(i18n / AI): placeholder starter questions shown under an expanded
// biomarker while we explore the interaction. Eventually these should be
// generated per biomarker (and translated); for now they're generic.
const AI_MARKER_QUESTIONS = [
  "What does a high result mean?",
  "What does a low result mean?",
  "How can I improve it?",
]

// TODO(i18n / AI): placeholder starter questions in the "Ask AI" dropdown.
const AI_STARTER_QUESTIONS = [
  "Which biomarkers matter most for heart health?",
  "What should I check if I'm often tired?",
  "What does a full-body MRI look at?",
  "How do I read my cholesterol results?",
]

/** Round check used on panel (single-select) and add-on (multi-select) rows:
 *  an empty ring when off, the design-system filled check-circle when on. */
function RoundCheck({ checked }: { checked: boolean }) {
  return checked ? (
    <CheckCircleNeutralIcon size={20} className="shrink-0 text-neutral-900" />
  ) : (
    <span
      aria-hidden="true"
      className="size-5 shrink-0 rounded-full border border-neutral-300 bg-white"
    />
  )
}

/** Count that tweens to its new value when it changes (micro-interaction on
 *  filter switches). Uses rAF, no layout thrash — renders a plain number. */
function AnimatedCount({ value }: { value: number }) {
  const [display, setDisplay] = useState(value)
  const fromRef = useRef(value)
  useEffect(() => {
    const from = fromRef.current
    const to = value
    if (from === to) return
    const start = performance.now()
    const duration = 320
    let raf = 0
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      setDisplay(Math.round(from + (to - from) * eased))
      if (t < 1) raf = requestAnimationFrame(tick)
      else fromRef.current = to
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value])
  return <>{display}</>
}

/** Per-category optical scale nudge — some organ icons sit smaller inside their
 *  viewBox than others, so scale them up to read consistently. */
const CATEGORY_ICON_SCALE: Record<string, string> = {
  metabolic: "[&_svg]:scale-[1.2]",
}

/**
 * Info tooltip that stays inside 5%–95% of the viewport horizontally.
 * The tooltip is positioned with `left: 0` on the parent's coordinate
 * system and shifted with an inline `translateX` computed from the
 * button's viewport position at mount and on resize.
 */
export function CategoryInfoTooltip({
  categoryId,
  editLocale,
  label,
  description,
  isOpen,
  onToggle,
}: {
  id: string
  /** Payload doc id — registers the inline-editor region. Absent → no region. */
  categoryId?: string
  /** CMS locale these docs were fetched in (region provenance). */
  editLocale: string
  label: string
  description: string
  isOpen: boolean
  onToggle: () => void
}) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const descriptionRef = useEditRegion<HTMLDivElement>(
    categoryId
      ? {
          collection: "categories",
          id: categoryId,
          field: "description",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const [translateX, setTranslateX] = useState(0)

  useLayoutEffect(() => {
    const update = () => {
      const btn = buttonRef.current
      if (!btn) return
      const rect = btn.getBoundingClientRect()
      const vw = window.innerWidth
      const tipWidth = vw >= 640 ? 320 : 288 // sm:w-80 / w-72
      const pad = vw * 0.05
      const buttonCenter = rect.left + rect.width / 2
      const defaultLeft = buttonCenter - tipWidth / 2
      const minLeft = pad
      const maxLeft = vw - pad - tipWidth
      const targetLeft = Math.max(minLeft, Math.min(defaultLeft, maxLeft))
      setTranslateX(targetLeft - rect.left)
    }
    update()
    window.addEventListener("resize", update)
    return () => window.removeEventListener("resize", update)
  }, [])

  return (
    <div
      className="group/info relative inline-flex"
      data-info-open={isOpen || undefined}
    >
      <button
        ref={buttonRef}
        type="button"
        className="hover:text-foreground cursor-help text-neutral-800 transition-colors"
        aria-label={`Info: ${label}`}
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
      </button>
      <div
        ref={descriptionRef}
        className="invisible absolute top-full left-0 z-40 mt-2 w-72 rounded-xl bg-white p-6 text-sm leading-relaxed text-neutral-600 opacity-0 shadow-lg transition-all duration-200 group-hover/info:visible group-hover/info:opacity-100 group-data-[info-open]/info:visible group-data-[info-open]/info:opacity-100 sm:w-80"
        style={{ transform: `translateX(${translateX}px)` }}
      >
        {description}
      </div>
    </div>
  )
}

export default function BiomarkersPageClient({
  biomarkers: allBiomarkers,
  cmsCategories,
  initialFilter: requestedFilter,
  loadError,
  country,
  chatEligible,
}: {
  biomarkers: CMSBiomarkerListItem[]
  cmsCategories: CmsCategory[]
  initialFilter?: string
  loadError?: boolean
  country: string
  /** Whether the Ella AI answer lane is offered on this market. */
  chatEligible: boolean
}) {
  // A filter route for a tier this market doesn't sell (/at/de/biomarkers/core,
  // also the target of the old body-composition redirects) opens on the
  // market's entry tier instead of a panel with no chip.
  const initialFilter =
    requestedFilter && unsoldMriTierKeys(country).includes(requestedFilter)
      ? entryMriTierKey(country)
      : requestedFilter
  // Germany sells the Vorsorge-Starter blood test and its blood add-ons, nothing else,
  // so the glossary carries no MRI / CT / DEXA surface at all: the Core /
  // Advanced / Pro panels leave the rail (they used to sit in the overflow as
  // "coming soon") along with the imaging add-ons, their "included in" chips
  // stop rendering, and the catalogue itself narrows to the markers a German
  // report actually comes back with. Listing a brain volume or a calcium score
  // on /de/de would be wrong, not merely untranslated. Every other market keeps
  // the full catalogue — including AT, which is a separate decision. Restore
  // this when DE_EXPANSION opens MRI booking in Germany.
  // country-gate-ignore: DE checkup-phase page shape, not a page gate
  const bloodOnly = country === "de"
  const biomarkers = useMemo(
    () =>
      bloodOnly
        ? allBiomarkers.filter(isBloodOnlyMarketBiomarker)
        : allBiomarkers,
    [allBiomarkers, bloodOnly]
  )

  const ut = useTranslations("ui")
  const bt = useTranslations("biomarkers")
  const ct = useTranslations("chat")
  const wt = useTranslations("whatWeDetect")
  const st = useTranslations("symptoms")
  const lp = useLocalePath()
  // Show the Symptoms peer tab when the lens is on (build-time env) OR the
  // visitor opted into the preview via `?symptoms-preview=` (companion cookie
  // read post-mount, SSR-safe — see lib/symptoms-preview.ts).
  const [symptomsTabVisible, setSymptomsTabVisible] =
    useState(areSymptomsEnabled())
  useEffect(() => {
    if (!areSymptomsEnabled() && hasSymptomsPreviewActive())
      setSymptomsTabVisible(true)
  }, [])
  const locale = useLocale()
  // Inline-editor provenance — CMS locale these docs were fetched in. The
  // editor registers regions client-side; no-op in public prod.
  const editLocale = cmsLocale(locale, useCountry().code)
  // Panel = single-select: you can only order one test tier, so it simply
  // scopes the whole list (no intersection). Add-ons are multi-select and
  // additive on top of the panel. DE has one bookable panel, so it opens on it
  // unless a filter route already set one.
  const [selectedPanel, setSelectedPanel] = useState<string | null>(() => {
    if (initialFilter && MAIN_PACKAGE_IDS.has(initialFilter))
      return initialFilter
    // country-gate-ignore: DE checkup-phase default, not a page gate
    if (country === "de") return "prevention-starter"
    return null
  })
  const [selectedAddons, setSelectedAddons] = useState<Set<string>>(
    () =>
      new Set(
        initialFilter && !MAIN_PACKAGE_IDS.has(initialFilter)
          ? [initialFilter]
          : []
      )
  )
  const [searchQuery, setSearchQuery] = useState("")
  const deferredSearch = useDeferredValue(searchQuery)
  const [expandedItem, setExpandedItem] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  // Ella AI drawer. `aiMode` flips the search into "ask" mode; opening the
  // drawer with a question streams the answer in a right-side panel.
  // AI-assistant gate: the build-time flag, OR — for preview/QA — the
  // `?ai-preview=<token>` magic link (cookie read post-hydration, SSR-safe so it
  // never forces the page dynamic). Mirrors the answer-lane's preview gate.
  const aiEnvEnabled = chatEligible && aiAssistantEnabled()
  const [aiEnabled, setAiEnabled] = useState(aiEnvEnabled)
  useEffect(() => {
    if (chatEligible && !aiEnvEnabled && hasAiPreviewActive())
      setAiEnabled(true)
  }, [chatEligible, aiEnvEnabled])

  // The canned "full-body MRI" prompt has no answer in a blood-only market.
  const aiStarterQuestions = bloodOnly
    ? AI_STARTER_QUESTIONS.filter((q) => !/\bMRI\b/.test(q))
    : AI_STARTER_QUESTIONS

  // Left-rail scroll fade — soft top/bottom masks that appear only when there
  // is more content to scroll to in that direction (the rail is its own
  // scroll container on md+; on mobile it doesn't scroll so no fade shows).
  const railRef = useRef<HTMLElement>(null)
  // Lets the mobile category strip pan with a mouse (drag + wheel), not just
  // touch. No-op on desktop where the rail is a vertical list.
  const catScrollRef = useHorizontalDragScroll<HTMLUListElement>()
  // Mobile only: tuck the tall search band away while scrolling down, slide it
  // back on scroll up. The filter dropdowns + category strip stay pinned and
  // rise to fill the freed space (see the toolbar classes below).
  const searchHidden = useHideOnScrollDown(railRef)
  const [railFade, setRailFade] = useState({ top: false, bottom: false })
  useEffect(() => {
    const el = railRef.current
    if (!el) return
    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = el
      setRailFade({
        top: scrollTop > 4,
        bottom: scrollTop + clientHeight < scrollHeight - 4,
      })
    }
    update()
    el.addEventListener("scroll", update, { passive: true })
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      el.removeEventListener("scroll", update)
      ro.disconnect()
    }
  }, [])
  const railMask = `linear-gradient(to bottom, transparent 0, black ${
    railFade.top ? "28px" : "0px"
  }, black calc(100% - ${railFade.bottom ? "28px" : "0px"}), transparent 100%)`

  const [aiMode, setAiMode] = useState(false)
  const [aiOpen, setAiOpen] = useState(false)
  const [aiQuestion, setAiQuestion] = useState<string | null>(null)
  const [aiAskNonce, setAiAskNonce] = useState(0)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const resultsCardRef = useRef<HTMLDivElement>(null)
  // Ask-AI panel opens inline (pushes the list down) and collapses back to a
  // slim field on scroll / outside-click. Mirrors the symptoms tab; the default
  // here stays Search mode (aiMode starts false above).
  const [aiPanelCollapsed, setAiPanelCollapsed] = useState(false)
  const searchFieldRef = useRef<HTMLDivElement>(null)

  // Collapse the open Ask-AI panel once the field is pinned at the top and the
  // content is scrolling under it. One-way: scrolling back up doesn't re-open —
  // a click/focus on the field or the Ask-AI toggle does.
  useEffect(() => {
    const onScroll = () => {
      const top =
        searchFieldRef.current?.getBoundingClientRect().top ?? Infinity
      if (top <= 88) setAiPanelCollapsed(true)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  // Clicking outside the field block (the list, the rail) collapses the panel.
  useEffect(() => {
    if (!aiEnabled) return
    const onPointerDown = (e: PointerEvent) => {
      const field = searchFieldRef.current
      if (field && !field.contains(e.target as Node)) setAiPanelCollapsed(true)
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [aiEnabled])

  const openAi = (question: string | null) => {
    setAiQuestion(question)
    setAiAskNonce((n) => n + 1)
    setAiOpen(true)
  }
  // Add-ons are collapsed by default (long list) — auto-open when the visitor
  // deep-links straight to an add-on filter so their selection is visible.
  const [addonsOpen, setAddonsOpen] = useState(
    () => !!initialFilter && !MAIN_PACKAGE_IDS.has(initialFilter)
  )

  const detailPrefix = lp("/biomarkers")

  // Effective filter set = the selected panel (if any) + every selected add-on.
  const selectedPackages = useMemo(() => {
    const s = new Set<string>(selectedAddons)
    if (selectedPanel) s.add(selectedPanel)
    return s
  }, [selectedPanel, selectedAddons])

  const selectPanel = (id: string) => {
    const next = selectedPanel === id ? null : id
    setSelectedPanel(next)
    window.history.pushState(
      {},
      "",
      next ? `${detailPrefix}/${next}` : detailPrefix
    )
  }

  const toggleAddon = (id: string) => {
    setSelectedAddons((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const clearAll = () => {
    setSelectedPanel(null)
    setSelectedAddons(new Set())
    setSearchQuery("")
    window.history.pushState({}, "", detailPrefix)
  }

  // `packageLabelById` is derived from this list, and `packageChips` only
  // renders a package it has a label for — so dropping the imaging filters
  // here also strips the Core / Advanced / Pro chips off every marker row.
  const translatedPackages = useMemo(
    () =>
      dropImagingFiltersForCountry(
        orderFiltersForCountry(
          buildPackageFilters(biomarkers, (k) => wt(k as never)),
          country
        ),
        country
      ),
    [biomarkers, wt, country]
  )
  const mainPackages = translatedPackages.filter((p) =>
    MAIN_PACKAGE_IDS.has(p.id)
  )
  const addonPackages = translatedPackages.filter(
    (p) => !MAIN_PACKAGE_IDS.has(p.id)
  )

  // How many biomarkers each package contains (for the checkbox counts).
  const packageCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const b of biomarkers)
      for (const p of b.packages) counts.set(p, (counts.get(p) ?? 0) + 1)
    return counts
  }, [biomarkers])

  // Tag-only packages (`addon-single`) label a row but never reach the rail, so
  // they're merged in below rather than derived from the filter list.
  const tagOnlyPackages = useMemo(
    () => buildTagOnlyPackages(biomarkers, (k) => wt(k as never)),
    [biomarkers, wt]
  )

  // id → translated label, for the "included in" chips on each biomarker row.
  const packageLabelById = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of translatedPackages) m.set(p.id, p.label)
    for (const p of tagOnlyPackages) m.set(p.id, p.label)
    return m
  }, [translatedPackages, tagOnlyPackages])

  const chipLabel = (id: string) => {
    const shortKey = SHORT_PACKAGE_LABEL_KEY[id]
    if (shortKey) return wt(shortKey as never)
    return packageLabelById.get(id) ?? id
  }

  // "Included in" chips — panels first. Collapsed rows cap at 3 with "+N";
  // an expanded row reveals the full membership.
  const packageChips = (pkgs: string[], expanded: boolean) => {
    const ordered = pkgs
      .filter((p) => packageLabelById.has(p))
      .sort(
        (a, b) =>
          (PACKAGE_ORDER_INDEX.get(a) ?? Infinity) -
          (PACKAGE_ORDER_INDEX.get(b) ?? Infinity)
      )
    if (ordered.length === 0) return null
    const shown = expanded ? ordered : ordered.slice(0, 3)
    const extra = ordered.length - shown.length
    return (
      <span className="hidden shrink-0 flex-wrap items-center justify-end gap-1.5 md:flex">
        {shown.map((id) => (
          <ActionBadge
            key={id}
            label={chipLabel(id)}
            color={PACKAGE_BADGE_COLOR[id] ?? "neutral"}
          />
        ))}
        {extra > 0 && (
          <span className="type-body-sm text-neutral-400">+{extra}</span>
        )}
      </span>
    )
  }

  // Fuzzy + alias-aware searcher, memoized so the Fuse index builds once per
  // mount. Cross-locale aliases (other locales' names, keyed by doc id) load lazily so
  // a German visitor searching the English term — and vice versa — still finds
  // the doc; until it lands, search works on the current locale's own names.
  const [crossLocaleAliases, setCrossLocaleAliases] =
    useState<CrossLocaleAliasMap>({})
  // Settled signal: the searchable set only mutates post-mount when the
  // aliases land (the `biomarkers` prop is server-rendered, already present).
  // Until then `filtered` is computed against the current-locale names only,
  // so a cross-locale term reads 0 before the aliases correct it — gating
  // analytics + the "no results" UI on this flag kills the spurious zero.
  const [aliasesReady, setAliasesReady] = useState(false)
  useEffect(() => {
    loadCrossLocaleAliases("biomarker").then((aliases) => {
      setCrossLocaleAliases(aliases)
      setAliasesReady(true)
    })
  }, [])

  const searcher = useMemo(
    () =>
      createPageSearcher(biomarkers, locale, {
        augmentAliases: (b) => crossLocaleAliases[String(b.id)] ?? [],
      }),
    [biomarkers, locale, crossLocaleAliases]
  )

  const filtered = useMemo(() => {
    // In "Ask AI" mode the typed text is a question, not a filter — the list
    // stays unfiltered by text (only the panel/add-on scope still applies).
    const searched =
      !aiMode && deferredSearch.trim()
        ? searcher.filter(deferredSearch)
        : biomarkers
    return selectedPackages.size > 0
      ? searched.filter((b) => b.packages.some((p) => selectedPackages.has(p)))
      : searched
  }, [biomarkers, selectedPackages, deferredSearch, searcher, aiMode])

  // Zero-result fallback: when the in-scope list is empty but the user is
  // searching, look across the WHOLE catalog (all packages) for the term.
  const crossMatches = useMemo(() => {
    const q = deferredSearch.trim()
    if (aiMode || !q || filtered.length > 0) return []
    return findCrossCatalogMatches({
      query: q,
      allItems: biomarkers,
      locale,
      augmentAliases: (b) => crossLocaleAliases[String(b.id)] ?? [],
      excludePackages: [...selectedPackages],
      country,
    })
  }, [
    aiMode,
    deferredSearch,
    filtered.length,
    biomarkers,
    locale,
    selectedPackages,
    crossLocaleAliases,
    country,
  ])

  const groupedCategories = useMemo(
    () => groupItemsByCmsCategory(filtered, cmsCategories),
    [filtered, cmsCategories]
  )

  // Items whose category has no CMS row — collected into a trailing "All"
  // section so an unseeded category never drops biomarkers off the page.
  const orphanItems = useMemo(() => {
    const grouped = new Set(cmsCategories.map((c) => c.slug))
    return filtered.filter((b) => !grouped.has(b.category))
  }, [filtered, cmsCategories])

  // The stacked sections + the left-rail nav are derived from the same list so
  // they never drift. Each section carries the CMS icon/colour for its rail row
  // and heading, plus an anchor slug used by the scroll-spy.
  const sections = useMemo<BiomarkerSection[]>(() => {
    const cats: BiomarkerSection[] = groupedCategories.map(
      ({ category, items }) => ({
        slug: category.slug,
        id: category.id,
        name: category.name,
        description: category.description ?? "",
        icon: category.icon,
        color: category.color,
        items,
      })
    )
    if (orphanItems.length > 0) {
      cats.push({
        slug: OTHER_SECTION_SLUG,
        name: wt("filterAll"),
        description: "",
        items: orphanItems,
      })
    }
    return cats
  }, [groupedCategories, orphanItems, wt])

  const searchTokens = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase()
    return q ? q.split(/\s+/) : undefined
  }, [deferredSearch])

  // ── Scroll-spy: highlight the section nearest the top of the viewport ──
  // The observer queries the DOM directly (not a ref map) so it always binds
  // to the elements actually committed for the current `sections` list.
  const orderedSlugs = sections.map((s) => s.slug)
  const orderedKey = orderedSlugs.join("|")
  const orderedSlugsRef = useRef<string[]>(orderedSlugs)
  orderedSlugsRef.current = orderedSlugs

  useEffect(() => {
    const els = Array.from(
      document.querySelectorAll<HTMLElement>("[data-section-slug]")
    )
    if (els.length === 0) return
    const visible = new Set<string>()
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const slug = (e.target as HTMLElement).dataset.sectionSlug
          if (!slug) continue
          if (e.isIntersecting) visible.add(slug)
          else visible.delete(slug)
        }
        const first = orderedSlugsRef.current.find((s) => visible.has(s))
        if (first) setActiveSection(first)
      },
      { rootMargin: "-160px 0px -55% 0px", threshold: 0 }
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [orderedKey])

  const scrollToSection = (slug: string) => {
    setActiveSection(slug)
    scrollSectionToTop(slug, railRef.current)
  }

  const activeSlug = activeSection ?? orderedSlugs[0]

  // When a text search first becomes active, bring the results to the top so
  // they read from the first match instead of leaving the viewport wherever the
  // (now much shorter) list happened to clamp. Only fires on the empty→active
  // transition, so refining an active query never yanks the page around.
  const searchActive = !aiMode && deferredSearch.trim().length > 0
  const prevSearchActiveRef = useRef(false)
  useEffect(() => {
    if (searchActive && !prevSearchActiveRef.current)
      scrollResultsIntoView(resultsCardRef.current)
    prevSearchActiveRef.current = searchActive
  }, [searchActive])

  // Same clamp, different trigger: picking a panel or add-on filters the list
  // shorter, so a visitor who had scrolled into it was dropped at the very end
  // of the page — past the results, in the insurance section. Re-land the card
  // on every change to the active filter set (mount excluded, so a deep-linked
  // /biomarkers/{package} still opens where the browser put it).
  const filterKey = [selectedPanel ?? "", ...[...selectedAddons].sort()].join(
    "|"
  )
  const prevFilterKeyRef = useRef(filterKey)
  useEffect(() => {
    if (prevFilterKeyRef.current === filterKey) return
    prevFilterKeyRef.current = filterKey
    scrollResultsIntoView(resultsCardRef.current)
  }, [filterKey])

  // Track searches (debounced; deduped on same query+count). Mirrors the FAQ
  // pattern — fires only after the user pauses typing.
  const lastTrackedSearchRef = useRef<{ query: string; count: number } | null>(
    null
  )
  useEffect(() => {
    if (!aliasesReady) return
    const q = deferredSearch.trim()
    if (aiMode || q.length < 3) return
    const handle = setTimeout(() => {
      const last = lastTrackedSearchRef.current
      if (last && last.query === q && last.count === filtered.length) return
      lastTrackedSearchRef.current = { query: q, count: filtered.length }
      track.searchQuery({
        query: q,
        result_count: filtered.length,
        surface: "biomarkers",
        locale,
        zero_results: filtered.length === 0,
      })
    }, 1200)
    return () => clearTimeout(handle)
  }, [aiMode, deferredSearch, filtered.length, locale, aliasesReady])

  const lastShownRef = useRef<string | null>(null)
  useEffect(() => {
    const q = deferredSearch.trim()
    if (crossMatches.length === 0) return
    if (lastShownRef.current === q) return
    lastShownRef.current = q
    track.searchSuggestionShown({
      query: q,
      surface: "biomarkers",
      match_count: crossMatches.length,
      type: "biomarkers",
      locale,
    })
  }, [crossMatches, deferredSearch, locale])

  const hasActiveFilters =
    selectedPackages.size > 0 || (!aiMode && searchQuery.trim() !== "")

  // A selectable filter row (panel or add-on) — round check + name + count.
  // Same visual footprint as the category rows; only the leading control differs.
  const renderFilterRow = (
    pkg: { id: string; label: string },
    checked: boolean,
    onToggle: () => void,
    role: "radio" | "checkbox"
  ) => (
    <li key={pkg.id}>
      <button
        type="button"
        onClick={onToggle}
        role={role}
        aria-checked={checked}
        className={cn(
          "group flex w-full cursor-pointer items-center gap-3 rounded-full px-3 py-2 text-left transition-colors",
          checked
            ? "bg-white text-neutral-900"
            : "text-neutral-700 hover:bg-white"
        )}
      >
        <RoundCheck checked={checked} />
        <span
          className={cn(
            "type-title-md min-w-0 flex-1 truncate",
            checked && "font-medium text-neutral-900"
          )}
        >
          {pkg.label}
        </span>
        <span className="type-body-md text-neutral-400">
          {packageCounts.get(pkg.id) ?? 0}
        </span>
      </button>
    </li>
  )

  // AI question chips shown under an expanded biomarker — click streams the
  // answer in the drawer, scoped to that marker. Ends with an "Ask Ella" chip.
  const markerAiChips = (name: string) => (
    <div className="mt-3 flex flex-wrap gap-2">
      {AI_MARKER_QUESTIONS.map((q) => (
        <button
          key={q}
          type="button"
          onClick={() => openAi(`${q} (${name})`)}
          className="type-body-sm cursor-pointer rounded-full border border-neutral-200 px-3 py-1.5 text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50"
        >
          {q}
        </button>
      ))}
      <button
        type="button"
        onClick={() => openAi(`Tell me about ${name}`)}
        className="pill-magic-stroke pill-magic-stroke-always type-body-sm inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-transparent px-3 py-1.5 font-medium text-neutral-900 transition-colors hover:bg-neutral-50"
      >
        <SparkleIcon width={15} height={15} className="text-aubergine-700" />
        {bt("askAi")}
      </button>
    </div>
  )

  // Panels: single-select (radio). Add-ons: multi-select (checkbox).
  const renderPanelRow = (pkg: { id: string; label: string }) =>
    renderFilterRow(
      pkg,
      selectedPanel === pkg.id,
      () => selectPanel(pkg.id),
      "radio"
    )
  const renderAddonRow = (pkg: { id: string; label: string }) =>
    renderFilterRow(
      pkg,
      selectedAddons.has(pkg.id),
      () => toggleAddon(pkg.id),
      "checkbox"
    )

  return (
    <>
      {/* Hero — white band separating the header (title + tabs) from the grey
          biomarker content below. The -mt/pt pair bleeds the white up behind
          the sticky navbar (same trick as the /go/* heroes) so the frosted
          navbar sits on white — no grey strip shows under the nav. */}
      <section className="relative -mt-[120px] -mb-px bg-white pt-[184px] pb-12 text-center md:pt-[200px] md:pb-6">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p className="type-label-lg text-neutral-400">{bt("label")}</p>
          <h1 className="type-display-lg mx-auto mt-4 max-w-3xl text-balance text-neutral-900">
            {bt.rich("heading", {
              highlight: (chunks) => (
                <span className="text-bright-blue-700">{chunks}</span>
              ),
            })}
          </h1>
          <p className="type-body-xl mx-auto mt-4 max-w-2xl text-neutral-600">
            {bt("description")}
          </p>
        </div>

        {/* Tab switcher — neutral segmented control (white active pill on a
            grey track), matching the rail's active-chip language. */}
        <div className="mt-8">
          <div className="inline-flex rounded-full bg-neutral-100 p-1">
            <Link
              href={lp("/what-we-detect")}
              prefetch={true}
              className="type-title-sm rounded-full px-5 py-2.5 text-neutral-500 transition-colors duration-200 hover:text-neutral-900"
            >
              {wt("tabHealthRisks")}
            </Link>
            <span className="type-title-sm rounded-full bg-white px-5 py-2.5 text-neutral-900">
              {wt("tabBiomarkers")}
            </span>
            {symptomsTabVisible ? (
              <Link
                href={lp("/symptoms")}
                prefetch={true}
                className="type-title-sm rounded-full px-5 py-2.5 text-neutral-500 transition-colors duration-200 hover:text-neutral-900"
              >
                {st("tabLabel")}
              </Link>
            ) : null}
          </div>
        </div>
      </section>

      {/* Explorer: left rail (panels + categories + add-ons) · right white card.
          The md top padding gives the sticky search's backing room to bleed up
          behind the navbar (see below) while staying invisible (grey-on-grey)
          at rest. */}
      <section className="pt-10 pb-20 md:pt-[80px]">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="grid grid-cols-1 gap-y-0 md:grid-cols-[280px_1fr] md:gap-x-8">
            {/* ── LEFT RAIL ── */}
            <aside
              ref={railRef}
              style={{ "--rail-mask": railMask } as React.CSSProperties}
              className={cn(
                "bg-background sticky top-[130px] z-20 order-2 flex flex-col gap-2 pt-2 pb-2 transition-transform duration-300 will-change-transform md:top-[72px] md:z-auto md:order-none md:col-start-1 md:row-span-2 md:row-start-1 md:max-h-[calc(100vh-88px)] md:translate-y-0 md:gap-6 md:self-start md:overflow-y-auto md:bg-transparent md:[mask-image:var(--rail-mask)] md:pt-2 md:pr-1 md:pb-0 md:[-webkit-mask-image:var(--rail-mask)]",
                // Follow the search up by its band height (64+8+58-8=66px) so the
                // filters + categories re-pin under the navbar (rail top-[130px] →
                // 64px) with the pt-2 giving breathing room above the panels row.
                // Mobile only (searchHidden is false at md+).
                searchHidden && "-translate-y-[66px]"
              )}
            >
              {/* Mobile-only: Panels + Add-ons collapse into dropdowns so the
                  long filter lists don't push the results down the page. Desktop
                  keeps the full vertical lists below (hidden on mobile). */}
              <MobileRailFilters
                panelsLabel={bt("panelsHeading")}
                addonsLabel={bt("addonsHeading")}
                mainPackages={mainPackages}
                addonPackages={addonPackages}
                selectedPanel={selectedPanel}
                selectedAddons={selectedAddons}
                onSelectPanel={selectPanel}
                onToggleAddon={toggleAddon}
                counts={packageCounts}
              />

              {/* Panels — single-select nav rows (like categories) */}
              {mainPackages.length > 0 && (
                <div className="hidden md:block">
                  <h2 className="type-label-md mb-2 px-2 text-neutral-600">
                    {bt("panelsHeading")}
                  </h2>
                  <ul className="flex flex-col gap-0.5">
                    {mainPackages.map(renderPanelRow)}
                  </ul>
                </div>
              )}

              {/* Add-ons — sit with the panels, both are package filters.
                  Collapsed behind a "show more" toggle (long list). */}
              {addonPackages.length > 0 && (
                <div className="hidden md:block">
                  <h2 className="type-label-md mb-2 px-2 text-neutral-600">
                    {bt("addonsHeading")}
                  </h2>
                  <ul className="flex flex-col gap-0.5">
                    {(addonsOpen
                      ? addonPackages
                      : addonPackages.slice(0, ADDON_PREVIEW_COUNT)
                    ).map(renderAddonRow)}
                  </ul>
                  {addonPackages.length > ADDON_PREVIEW_COUNT && (
                    <button
                      type="button"
                      onClick={() => setAddonsOpen((o) => !o)}
                      aria-expanded={addonsOpen}
                      className="mt-1.5 flex w-full cursor-pointer items-center gap-3 px-2 text-neutral-500 transition-colors hover:text-neutral-900"
                    >
                      <span className="h-px flex-1 bg-neutral-200" />
                      <span className="type-body-md inline-flex items-center gap-1">
                        {addonsOpen
                          ? bt("showLess")
                          : `${ut("showMore")} (${addonPackages.length - ADDON_PREVIEW_COUNT})`}
                        <ChevronDownSmIcon
                          className={cn(
                            "transition-transform duration-200",
                            addonsOpen && "rotate-180"
                          )}
                        />
                      </span>
                      <span className="h-px flex-1 bg-neutral-200" />
                    </button>
                  )}
                </div>
              )}

              {/* Categories — scroll-spy nav */}
              {sections.length > 0 && (
                <div>
                  {/* Heading hidden on mobile to save vertical space in the
                      pinned toolbar; the horizontal chip row is self-evident. */}
                  <h2 className="type-label-md mb-2 hidden px-2 text-neutral-600 md:block">
                    {bt("categoriesHeading")}
                  </h2>
                  <ul
                    ref={catScrollRef}
                    // Mobile: full-bleed to the viewport edges (-mx-4 cancels the
                    // container px-4), with px-4 scroll-padding so chips rest
                    // aligned with the content above but scroll to the very edge.
                    // Reset to the vertical list at md+.
                    className="-mx-4 flex cursor-grab [scrollbar-width:none] gap-2 overflow-x-auto px-4 active:cursor-grabbing md:mx-0 md:cursor-auto md:flex-col md:gap-0.5 md:overflow-x-visible md:px-0 [&::-webkit-scrollbar]:hidden"
                  >
                    {sections.map((s) => {
                      const isActive = activeSlug === s.slug
                      return (
                        <li key={s.slug} className="shrink-0 md:w-full">
                          <button
                            type="button"
                            onClick={() => scrollToSection(s.slug)}
                            aria-current={isActive ? "true" : undefined}
                            className={cn(
                              "group flex w-fit cursor-pointer items-center gap-3 rounded-full px-4 py-2 text-left whitespace-nowrap transition-colors md:w-full md:px-3",
                              isActive
                                ? "bg-white text-neutral-900"
                                : "bg-white/60 text-neutral-700 hover:bg-white md:bg-transparent"
                            )}
                          >
                            <span
                              className={cn(
                                "hidden size-6 shrink-0 md:block [&_svg]:h-full [&_svg]:w-full [&_svg]:overflow-visible",
                                CATEGORY_ICON_SCALE[s.slug]
                              )}
                            >
                              {renderCategoryIcon({
                                categoryId: s.slug,
                                cmsIcon: s.icon,
                                // Neutral: category icons inherit currentColor
                                // (color is reserved for the MRI package dots).
                                cmsColor: null,
                                size: 24,
                              })}
                            </span>
                            <span
                              className={cn(
                                "type-title-sm md:type-title-md min-w-0 flex-1 truncate",
                                isActive && "font-medium"
                              )}
                            >
                              {s.name}
                            </span>
                            <span className="type-body-md hidden text-neutral-400 md:block">
                              <AnimatedCount value={s.items.length} />
                            </span>
                          </button>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="type-body-sm self-start px-2 text-neutral-500 underline-offset-2 transition-colors hover:text-neutral-900 hover:underline"
                >
                  {bt("clearAll")}
                </button>
              )}
            </aside>

            {/* ── SEARCH ── Its own grid child so mobile can hoist it above
                everything (order-1) while desktop keeps it atop the right column
                (col 2 / row 1). In "Ask AI" mode the rounded container expands
                downward as an overlay (absolute) with starter questions, so
                the list below doesn't shift. DS magic stroke + blue glow.
                Sticky on md+. The navbar is frosted (translucent), so plain
                content scrolling under it shows through — to stop that on this
                page WITHOUT touching the global nav, the opaque bg-background
                backing bleeds all the way to the viewport top (sticks at
                top-0, pt-[80px] lands the pill flush at the nav's 80px bottom).
                The frosted nav then blurs over solid grey, not content. The
                -mt-[80px] pulls that extra 80px band back up into the explorer's
                matching top padding so it's invisible (grey-on-grey) at rest;
                the gradient scrim below fades the card as it scrolls under. */}
            <div
              className={cn(
                "bg-background sticky top-[64px] z-30 order-1 pt-2 transition-[transform,opacity] duration-300 will-change-transform md:top-0 md:order-none md:col-start-2 md:row-start-1 md:-mt-[80px] md:translate-y-0 md:bg-transparent md:pt-0 md:pb-0 md:opacity-100",
                // Mobile auto-hide: slide up behind the navbar + fade while
                // scrolling down; never applies at md+ (searchHidden is false).
                searchHidden &&
                  "pointer-events-none -translate-y-full opacity-0"
              )}
            >
              <div className="md:bg-background md:pt-[80px]">
                <div ref={searchFieldRef} className="relative z-20">
                  <div
                    className={cn(
                      // Keep a 1px border in BOTH modes (transparent in AI) so the
                      // box model never changes when toggling — otherwise the field
                      // contents jump by 1px. The gradient border in AI mode is the
                      // `pill-magic-stroke` ::before overlay, not this border.
                      // Inline (no `absolute`) so the Ask-AI panel pushes the list
                      // down instead of overlaying it; `relative z-20` on the wrapper
                      // keeps the field's outline above the results card.
                      "rounded-[28px] border bg-white transition-[box-shadow,border-color]",
                      aiMode
                        ? "pill-magic-stroke pill-magic-stroke-activate ai-search-glow border-transparent"
                        : "border-neutral-200 focus-within:border-neutral-400 focus-within:ring-4 focus-within:ring-neutral-200/60"
                    )}
                  >
                    <label
                      className={cn(
                        "flex h-14 cursor-text items-center gap-3 pl-4",
                        aiEnabled ? "pr-2" : "pr-4"
                      )}
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center text-neutral-500">
                        {aiMode ? (
                          <SparkleIcon width={20} height={20} />
                        ) : (
                          <SearchIcon width={20} height={20} />
                        )}
                      </span>
                      <input
                        ref={searchInputRef}
                        type="search"
                        aria-label={
                          aiMode
                            ? bt("aiSearchPlaceholder")
                            : bt("searchPlaceholder")
                        }
                        autoComplete="off"
                        placeholder={
                          aiMode
                            ? bt("aiSearchPlaceholder")
                            : bt("searchPlaceholder")
                        }
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setAiPanelCollapsed(false)}
                        // A prior scroll can collapse the panel while the
                        // field keeps focus — so a plain focus won't fire on
                        // the next click. Re-open on pointer-down too.
                        onPointerDown={() => setAiPanelCollapsed(false)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && aiMode) {
                            e.preventDefault()
                            if (searchQuery.trim()) openAi(searchQuery.trim())
                          }
                        }}
                        className="type-body-xl min-w-0 flex-1 bg-transparent text-neutral-900 outline-none placeholder:text-neutral-400 [&::-webkit-search-cancel-button]:appearance-none"
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery("")}
                          aria-label={ut("clearSearch")}
                          className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-200/60 hover:text-neutral-900"
                        >
                          <CloseIcon width={18} height={18} />
                        </button>
                      )}
                      {aiEnabled && (
                        <SearchModeToggle
                          aiMode={aiMode}
                          onSearch={() => setAiMode(false)}
                          onAskAi={() => {
                            setAiMode(true)
                            setAiPanelCollapsed(false)
                            searchInputRef.current?.focus()
                          }}
                          searchLabel={bt("searchMode")}
                          askAiLabel={bt("askAi")}
                        />
                      )}
                    </label>

                    {/* Starter questions — inline reveal (pushes the list down),
                          collapses on scroll/outside-click like the symptoms tab */}
                    {aiEnabled && (
                      <div
                        className={cn(
                          "grid transition-[grid-template-rows] duration-300 ease-out",
                          aiMode && !aiPanelCollapsed
                            ? "grid-rows-[1fr]"
                            : "grid-rows-[0fr]"
                        )}
                      >
                        <div className="overflow-hidden">
                          <div className="border-t border-neutral-100 px-3 pt-2.5 pb-2.5">
                            <p className="type-label-md px-2.5 pb-1.5 text-neutral-400">
                              {bt("aiSuggestionsLabel")}
                            </p>
                            <div className="flex flex-col gap-0.5">
                              {aiStarterQuestions.map((q) => (
                                <button
                                  key={q}
                                  type="button"
                                  onClick={() => openAi(q)}
                                  className="type-body-lg flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-3 text-left text-neutral-700 transition-colors hover:bg-neutral-50"
                                >
                                  <SparkleIcon
                                    width={17}
                                    height={17}
                                    className="shrink-0 text-neutral-400"
                                  />
                                  {q}
                                </button>
                              ))}
                            </div>
                            <p className="type-body-sm px-3 pt-2 text-neutral-400">
                              {ct("assistant.disclaimer")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Soft shade — the biomarker card fades out as it scrolls up
                  under the pinned search, matching the rail's edge mask. */}
              <div
                aria-hidden
                className="from-background pointer-events-none hidden h-6 bg-gradient-to-b to-transparent md:block"
              />
            </div>

            {/* ── RESULTS ── count + stacked card. Follows the rail on mobile
                (order-3); sits in col 2 / row 2 on desktop. overflow-anchor:none
                stops the browser scroll-anchoring to a biomarker row filtered
                out mid-search — which yanked the page toward the top on every
                result change. */}
            <div className="order-3 flex flex-col [overflow-anchor:none] md:col-start-2 md:row-start-2">
              {/* Result count — only while a filter/search is active */}
              {hasActiveFilters && (
                <div className="type-body-md mb-4 px-1 text-neutral-500">
                  <AnimatedCount value={filtered.length} />{" "}
                  {bt("resultsLabel", { count: filtered.length })}
                </div>
              )}

              {/* ── White content card (stacked category sections) ── */}
              <div
                ref={resultsCardRef}
                className="flex flex-col rounded-[24px] bg-white p-5 md:p-8"
              >
                {/* Stacked category sections — single column */}
                {sections.map((s) => (
                  <div
                    key={s.slug}
                    id={s.slug}
                    data-section-slug={s.slug}
                    className="mb-12 scroll-mt-[160px] last:mb-0"
                  >
                    <div className="mb-3 flex items-center gap-3">
                      <span
                        className={cn(
                          "size-8 shrink-0 [&_svg]:h-full [&_svg]:w-full [&_svg]:overflow-visible",
                          CATEGORY_ICON_SCALE[s.slug]
                        )}
                      >
                        {renderCategoryIcon({
                          categoryId: s.slug,
                          cmsIcon: s.icon,
                          // Neutral: match the rail — color is reserved for the
                          // MRI package dots, not category icons.
                          cmsColor: null,
                          size: 32,
                        })}
                      </span>
                      {s.id ? (
                        <EditableField
                          collection="categories"
                          id={s.id}
                          field="name"
                          locale={editLocale}
                          kind="scalar"
                          as="h2"
                          className="type-title-lg leading-[130%] text-neutral-900"
                        >
                          {s.name}
                        </EditableField>
                      ) : (
                        <h2 className="type-title-lg leading-[130%] text-neutral-900">
                          {s.name}
                        </h2>
                      )}
                      <span className="text-sm text-neutral-400">
                        {bt("biomarkerCount", { count: s.items.length })}
                      </span>
                    </div>
                    {/* Category description — shown as a visible paragraph under
                        the heading (not tucked in an info tooltip). Editable in
                        place for the inline editor when the CMS id is present. */}
                    {s.description &&
                      (s.id ? (
                        <EditableField
                          collection="categories"
                          id={s.id}
                          field="description"
                          locale={editLocale}
                          kind="scalar"
                          as="p"
                          className="type-body-lg mb-5 max-w-2xl leading-relaxed text-neutral-500"
                        >
                          {s.description}
                        </EditableField>
                      ) : (
                        <p className="type-body-lg mb-5 max-w-2xl leading-relaxed text-neutral-500">
                          {s.description}
                        </p>
                      ))}
                    <div>
                      {s.items.map((biomarker) => (
                        <ExpandableRow
                          key={biomarker.slug}
                          name={biomarker.name}
                          slug={biomarker.slug}
                          description={biomarker.description}
                          expandedKey={biomarker.slug}
                          expanded={expandedItem}
                          onToggle={setExpandedItem}
                          detailPrefix={detailPrefix}
                          learnMoreLabel={`${ut("moreAbout")} ${biomarker.name}`}
                          accent="neutral"
                          searchTokens={searchTokens}
                          trailing={packageChips(
                            biomarker.packages,
                            expandedItem === biomarker.slug
                          )}
                          expandedContent={
                            aiEnabled
                              ? markerAiChips(biomarker.name)
                              : undefined
                          }
                          className="[&_p]:text-[1rem] [&>a]:py-4 [&>button]:py-4"
                        />
                      ))}
                    </div>
                  </div>
                ))}

                {filtered.length === 0 && loadError && (
                  <div className="py-20 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-neutral-400"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </div>
                    <p className="text-lg text-neutral-500">
                      {bt("loadError")}
                    </p>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="text-aubergine-600 mt-3 cursor-pointer text-sm font-medium hover:underline"
                    >
                      {bt("retry")}
                    </button>
                  </div>
                )}

                {filtered.length === 0 &&
                  !loadError &&
                  crossMatches.length > 0 && (
                    <CrossCatalogSuggestion
                      type="biomarkers"
                      match={crossMatches[0]}
                      itemHref={lp(`/biomarkers/${crossMatches[0].item.slug}`)}
                      testHref={(slug) => lp(`/tests/${slug}`)}
                      scopeLabel={
                        selectedPackages.size === 1
                          ? translatedPackages.find(
                              (p) => p.id === [...selectedPackages][0]
                            )?.label
                          : undefined
                      }
                      onClearScope={
                        selectedPackages.size > 0
                          ? () => {
                              track.searchSuggestionClicked({
                                query: deferredSearch.trim(),
                                surface: "biomarkers",
                                target_test: crossMatches[0].tests[0].slug,
                                target_type: "clear-scope",
                              })
                              clearAll()
                            }
                          : undefined
                      }
                      onSelect={(target) =>
                        track.searchSuggestionClicked({
                          query: deferredSearch.trim(),
                          surface: "biomarkers",
                          target_test: crossMatches[0].tests[0].slug,
                          target_type: target,
                        })
                      }
                    />
                  )}

                {filtered.length === 0 &&
                  !loadError &&
                  crossMatches.length === 0 && (
                    <div className="py-20 text-center">
                      <p className="text-lg text-neutral-500">
                        {bt("noResults")}
                      </p>
                      <button
                        type="button"
                        onClick={clearAll}
                        className="text-aubergine-600 mt-3 cursor-pointer text-sm font-medium hover:underline"
                      >
                        {bt("clearFilters")}
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {aiEnabled && (
        <BiomarkersAiDrawer
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          question={aiQuestion}
          askNonce={aiAskNonce}
          scope="biomarkers"
          country={country}
          locale={locale}
          pageUrl={lp("/biomarkers")}
          pageTitle={getPageTitle("biomarkers", locale, country)}
        />
      )}

      <InsuranceSection trackingLocation="biomarkers" />

      {/* Hidden navigation for crawlers — all biomarker detail links present in initial HTML */}
      <nav aria-label="All biomarkers" className="sr-only">
        {biomarkers.map((b) => (
          <Link key={b.slug} href={lp(`/biomarkers/${b.slug}`)}>
            {b.name}
          </Link>
        ))}
      </nav>
    </>
  )
}
